import classNames from 'classnames'
import { JSX, useEffect, useRef } from 'react'

import styles from './CheckboxBase.module.scss'

export interface ICheckboxCommonProps {
  /**
   * Text to the right of the marker
   */
  label?: string | JSX.Element
  /**
   * Is Checkbox checked
   */
  checked: boolean
  /**
   * Handler for Checkbox click event
   */
  onChange?: (value: boolean) => void
  /**
   * Spacing size under the RadioButton
   */
  margin?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Inner spacing size
   */
  padding?: boolean
  /**
   * Size of the checkbox
   */
  size?: 'md' | 'sm'
  /**
   * Checkbox has inline-flex layout
   */
  inline?: boolean
  /**
   * Checkbox is not available for interaction
   */
  disabled?: boolean
  /**
   * Sets text and marker color
   */
  wrapper?: 'normal' | 'warning' | 'danger'
  /**
   * Root test ID. This property is used for automated tests.
   */
  __testId?: string
}

interface ICheckboxBaseProps extends ICheckboxCommonProps {
  classes?: {
    root?: string
    inner?: string
    icon?: string
    iconChecked?: string
    iconIndeterminate?: string
    iconDisabled?: string
    control?: string
    label?: string
  }
  indeterminate?: boolean
  testid: string
  testidInput: string
  startLabel?: string
}

export const CheckboxBase = ({
  label,
  onChange,
  checked,
  margin,
  padding,
  startLabel,
  size = 'md',
  inline,
  disabled,
  wrapper,
  classes,
  indeterminate,
  testid,
  testidInput,
}: ICheckboxBaseProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const clickHandle = (ref: any) => {
    ref.current.click()
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate
    }
  }, [indeterminate])

  return (
    <div
      className={classNames(
        styles.root,
        {
          [styles[`margin-${margin}`]]: margin,
          [styles['root--padding']]: padding,
          [styles[`size-sm`]]: size === 'sm',
          [styles['root--inline']]: inline,
          [styles[`root--${wrapper}`]]: wrapper,
        },
        classes?.root
      )}
    >
      <div
        data-testid={testid}
        className={classNames(styles.inner, classes?.inner, disabled && styles['inner--disabled'])}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          clickHandle(inputRef)
        }}
        onClick={(e) => {
          if (e.target === inputRef.current) {
            e.stopPropagation()
            return
          }
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        {startLabel ? (
          <div className={classNames(styles.label, styles.label__start, classes?.label)}>
            {startLabel}
          </div>
        ) : null}
        <input
          ref={inputRef}
          data-testid={testidInput}
          className={classNames(styles.control, classes?.control)}
          type={'checkbox'}
          checked={checked}
          onChange={(e) => onChange && onChange(e.target.checked)}
          disabled={disabled}
        />
        <div
          className={classNames(
            styles.icon,
            classes?.icon,
            checked && classes?.iconChecked,
            indeterminate && classes?.iconIndeterminate,
            disabled && classes?.iconDisabled
          )}
        />
        {label && (
          <div className={classNames(styles.label, styles.label__end, classes?.label)}>{label}</div>
        )}
      </div>
    </div>
  )
}
